import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { FileType, PublicDevelopmentManagerDetailsParams } from "../../../../../models";
import { getDevelopmentManagerDetails } from "../../../../../service/publicquery";
import { Status } from "../../../../../service/Shared";
import { DevelopmentManagerDetailsData } from "../../../../../templates";

interface UseDetailsReturnData {
  dataIsLoading: boolean;
  developmentManagerDetailsData: DevelopmentManagerDetailsData | undefined;
}

export const useDetails = (): UseDetailsReturnData => {
  const { developmentManagerUuid } = useParams<PublicDevelopmentManagerDetailsParams>();

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [developmentManagerDetailsData, setDevelopmentManagerDetailsData] = useState<
    DevelopmentManagerDetailsData | undefined
  >(undefined);

  const fetchData = useCallback(async (): Promise<void> => {
    if (developmentManagerUuid) {
      const res = await getDevelopmentManagerDetails({
        developmentManagerUuid,
      });

      if (res.status === Status.Success && res.data) {
        const logo = res.data.files.find((f) => f.type === FileType.LargeLogo)?.file.url || null;
        const listingFiles: { uuid: string; file: { url: string } }[] = res.data.listingFiles ?? [];

        setDevelopmentManagerDetailsData({
          displayName: res.data.displayName,
          logo,
          websiteUrl: res.data.websiteUrl,
          contactPhone: res.data.contactPhone,
          calculatedContactable: res.data.calculatedContactable,
          socialMediaData: {
            twitterUsername: res.data.socialMediaTwitterUsername,
            linkedinURL: res.data.socialMediaLinkedInUrl,
            instagramUsername: res.data.socialMediaInstagramUsername,
            youtubeChannel: res.data.socialMediaYoutubeChannel,
            facebookURL: res.data.socialMediaFacebookUrl,
          },
          hero: {
            enabled: res.data.listing?.content?.hero ? res.data.listing?.content?.hero?.enabled : false,
            title: res.data.listing?.content?.hero?.title || null,
            subtitle: res.data.listing?.content?.hero?.subtitle || null,
            imageUrl:
              listingFiles.find((file) => file.uuid === res.data?.listing?.content?.hero?.imageFileUuid)?.file.url ||
              null,
          },
          atAGlance: {
            enabled: res.data.listing?.content?.atAGlance ? res.data.listing?.content?.atAGlance?.enabled : false,
          },
          aboutUs: {
            enabled: res.data.listing?.content?.aboutUs ? res.data.listing?.content?.aboutUs?.enabled : false,
            content: res.data?.listing?.content?.aboutUs?.content,
            imageUrl:
              listingFiles.find((file) => file.uuid === res.data?.listing?.content?.aboutUs?.imageFileUuid)?.file.url ||
              null,
          },
          ourServices: {
            enabled: res.data.listing?.content?.ourServices ? res.data.listing?.content?.ourServices?.enabled : false,
            part1: res.data.listing?.content?.ourServices?.part1
              ? {
                  title: res.data.listing?.content?.ourServices?.part1?.title,
                  content: res.data.listing?.content?.ourServices?.part1?.content,
                  imageUrl:
                    listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part1?.imageFileUuid
                    )?.file.url || null,
                }
              : null,
            part2: res.data.listing?.content?.ourServices?.part2
              ? {
                  title: res.data.listing?.content?.ourServices?.part2?.title,
                  content: res.data.listing?.content?.ourServices?.part2?.content,
                  imageUrl:
                    listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part2?.imageFileUuid
                    )?.file.url || null,
                }
              : null,
            part3: res.data.listing?.content?.ourServices?.part3
              ? {
                  title: res.data.listing?.content?.ourServices?.part3?.title,
                  content: res.data.listing?.content?.ourServices?.part3?.content,
                  imageUrl:
                    listingFiles.find(
                      (file) => file.uuid === res.data?.listing?.content?.ourServices?.part3?.imageFileUuid
                    )?.file.url || null,
                }
              : null,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchData().then(() => setDataIsLoading(false));
  }, [fetchData]);

  return {
    dataIsLoading,
    developmentManagerDetailsData,
  };
};
